/**
 * Brazilian States
 *
 * Consolidated file for all Brazilian state-related constants.
 * Values synced with backend BrazilianStateTypeEnum.
 * @see backend/src/backend/db/constants/brazilian_state.py
 *
 * For select components, use DmsBrazilianStateSelect from DmsEnumSelect.
 */

/**
 * List of state abbreviations (UFs)
 */
export const UFs = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
    "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
    "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

/**
 * State names by code
 */
export const STATE_NAMES = {
    AC: "Acre",
    AL: "Alagoas",
    AP: "Amapá",
    AM: "Amazonas",
    BA: "Bahia",
    CE: "Ceará",
    DF: "Distrito Federal",
    ES: "Espírito Santo",
    GO: "Goiás",
    MA: "Maranhão",
    MT: "Mato Grosso",
    MS: "Mato Grosso do Sul",
    MG: "Minas Gerais",
    PA: "Pará",
    PB: "Paraíba",
    PR: "Paraná",
    PE: "Pernambuco",
    PI: "Piauí",
    RJ: "Rio de Janeiro",
    RN: "Rio Grande do Norte",
    RS: "Rio Grande do Sul",
    RO: "Rondônia",
    RR: "Roraima",
    SC: "Santa Catarina",
    SP: "São Paulo",
    SE: "Sergipe",
    TO: "Tocantins"
};

/**
 * State flag paths by code
 */
export const STATE_FLAGS = {
    AC: "/flags/AC.svg",
    AL: "/flags/AL.svg",
    AP: "/flags/AP.svg",
    AM: "/flags/AM.svg",
    BA: "/flags/BA.svg",
    CE: "/flags/CE.svg",
    DF: "/flags/DF.svg",
    ES: "/flags/ES.svg",
    GO: "/flags/GO.svg",
    MA: "/flags/MA.svg",
    MT: "/flags/MT.svg",
    MS: "/flags/MS.svg",
    MG: "/flags/MG.svg",
    PA: "/flags/PA.svg",
    PB: "/flags/PB.svg",
    PR: "/flags/PR.svg",
    PE: "/flags/PE.svg",
    PI: "/flags/PI.svg",
    RJ: "/flags/RJ.svg",
    RN: "/flags/RN.svg",
    RS: "/flags/RS.svg",
    RO: "/flags/RO.svg",
    RR: "/flags/RR.svg",
    SC: "/flags/SC.svg",
    SP: "/flags/SP.svg",
    SE: "/flags/SE.svg",
    TO: "/flags/TO.svg"
};

/**
 * Combined state data with code, name and label
 */
export const brazilianStates = Object.fromEntries(
    UFs.map(code => [code, `${code} - ${STATE_NAMES[code]}`])
);

/**
 * Get label for a Brazilian state code
 * @param {string} code - State abbreviation (e.g., "SP")
 * @returns {string} Full label (e.g., "SP - São Paulo") or code if not found
 */
export const getBrazilianStateLabel = (code) => {
    return brazilianStates[code] || code;
};

/**
 * Get state name without abbreviation
 * @param {string} code - State abbreviation (e.g., "SP")
 * @returns {string} State name (e.g., "São Paulo") or code if not found
 */
export const getBrazilianStateName = (code) => {
    return STATE_NAMES[code] || code;
};

/**
 * Get state flag path
 * @param {string} code - State abbreviation (e.g., "SP")
 * @returns {string} Flag image path or empty string if not found
 */
export const getBrazilianStateFlag = (code) => {
    return STATE_FLAGS[code] || "";
};

/**
 * Get state options for select components
 * @returns {Array} Array of {value, label} objects
 */
export const getBrazilianStateOptions = () => {
    return UFs.map(code => ({
        value: code,
        label: brazilianStates[code]
    }));
};

/**
 * Get state options with flag data for enhanced select components
 * @returns {Array} Array of {value, label, name, flag} objects
 */
export const getBrazilianStateOptionsWithFlags = () => {
    return UFs.map(code => ({
        value: code,
        label: brazilianStates[code],
        name: STATE_NAMES[code],
        flag: STATE_FLAGS[code]
    }));
};
